/**
 * Central API contract for the LK Storekeeper app.
 *
 * This file is the single source of truth for:
 *  - route paths used by both the client (fetch calls) and the server (route handlers)
 *  - auth/session settings (cookie name, token lifetime)
 *  - roles
 *  - auto-discovery of the Postgres tables exposed through the generic data API,
 *    plus an optional per-table override list for labels / primary key / writable
 *    roles.
 *
 * The data API itself is generic/universal: tables present in the configured
 * schema are discovered at runtime (via information_schema) and returned to the
 * client, which renders a matching table + form. To customize a table's label,
 * primary key, or who may write to it, add an entry to `tables` (the override
 * list) below. Tables not in the override list are still exposed, using sensible
 * defaults derived from the schema.
 */

import type { Locale } from "@/i18n/config";
import { defaultLocale, locales } from "@/i18n/config";

// NOTE: these values must match the `role` column of the `users` table in the
// real database. The seed.sql uses 'admin', but the production-style DB uses
// 'administrator' — adjust here to match your actual data.
export const ROLES = {
  ADMIN: "administrator",
  STOREKEEPER: "storekeeper",
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export interface TableConfig {
  /** Actual Postgres table name (must exist in the configured schema). */
  name: string;
  /** Human friendly label shown in the UI, per locale. */
  labels: Record<Locale, string>;
  /** Primary key column, used for edit/update requests. Defaults to "id". */
  primaryKey: string;
  /** Roles allowed to add/edit rows in this table. Anyone authenticated can view. */
  writableRoles: Role[];
  /**
   * Column whose value is used as the human-readable label when this table's
   * rows appear as options in a foreign-key <select> (see the
   * /api/tables/:table/options endpoint). If omitted, the first non-PK
   * character-varying/text column is used, falling back to the primary key.
   */
  displayColumn?: string;
  /**
   * Columns whose values are masked in API responses (shown as
   * `MASKED_PLACEHOLDER`) and excluded from write payloads / the edit form,
   * but which still appear as a column in the table view. Merged with
   * `discovery.maskedColumns`.
   */
  maskedColumns?: string[];
}

/** Placeholder shown in place of a masked column's real value. */
export const MASKED_PLACEHOLDER = "***hidden***";

/**
 * Turn a snake_case table name into a human-readable label, e.g.
 * `receiving_operations` -> `Receiving operations`.
 */
export function humanizeTableLabel(name: string): string {
  const spaced = name.replace(/_/g, " ").trim();
  if (!spaced) return name;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Build a per-locale label record from a humanized table name. */
export function defaultLabelsFor(name: string): Record<Locale, string> {
  const label = humanizeTableLabel(name);
  return locales.reduce((acc, l) => {
    acc[l] = label;
    return acc;
  }, {} as Record<Locale, string>);
}

export const API_CONFIG = {
  /**
   * Auto-discovery settings.
   *
   * `schema`: the Postgres schema to introspect for tables to expose.
   * `excludeTables`: table names in that schema that should NOT be exposed
   *   (e.g. migration tracking or tables with sensitive columns).
   * `defaultWritableRoles`: roles that may write to any discovered table that
   *   is not listed in the `tables` override list below. Tables with a
   *   composite / missing primary key are always read-only regardless.
   */
  discovery: {
    schema: "public",
    excludeTables: [
      "alembic_version", // migration tracking, not business data
    ],
    /**
     * Columns whose values are masked in API responses (shown as
     * `MASKED_PLACEHOLDER`) and excluded from write payloads / the edit form,
     * but which still appear as a column in the table view. Per-table
     * additions can be made via `maskedColumns` on a `tables` override entry.
     */
    maskedColumns: ["password_hash"],
    defaultWritableRoles: [ROLES.ADMIN] as Role[],
  },

  /**
   * Optional override list. Add an entry here to customize a discovered
   * table's label, primary key, or writable roles. Tables not listed here are
   * still exposed using defaults derived from the schema.
   *
   * Example:
   *   {
   *     name: "warehouses",
   *     labels: { en: "Warehouses", ru: "Склады", zh: "仓库" },
   *     primaryKey: "id",
   *     writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
   *   },
   */
  tables: [
    {
      name: "forwarding_orders",
      labels: { en: "Forwarding orders", ru: "ПорученияЭкспедитору", zh: "委托单" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
      displayColumn: "number",
    },
    {
      name: "expected_arrivals",
      labels: { en: "Expected arrivals", ru: "РеестрОжидаемыхПоступлений", zh: "预计到货" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      name: "counterparties",
      labels: { en: "Counterparties", ru: "Контрагенты", zh: "合作方" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN],
      displayColumn: "name",
    },
    {
      // Отправители — suppliers of expected arrivals, sent by 1C. Read-only:
      // a direct write would skip the backend's 1C queue.
      name: "senders",
      labels: { en: "Senders", ru: "Отправители", zh: "发货人" },
      primaryKey: "id",
      writableRoles: [],
      displayColumn: "name",
    },
    {
      name: "packaging_types",
      labels: { en: "Packaging types", ru: "ВидыУпаковки", zh: "包装类型" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN],
      displayColumn: "name",
    },
    {
      // Exchanged with 1C: a direct write here would skip the backend's 1C
      // queue, so it is read-only (edit via the backend API).
      name: "handling_conditions",
      labels: { en: "Handling conditions", ru: "УсловияОбработки", zh: "操作条件" },
      primaryKey: "id",
      writableRoles: [],
      displayColumn: "name",
    },
    {
      // A rename must re-derive the ПЭ text for 1C (backend API): read-only.
      name: "cargo_receiving_conditions",
      labels: {
        en: "Cargo conditions at receiving",
        ru: "ФактическиеСостоянияГрузаПриПриеме",
        zh: "收货时货物状态",
      },
      primaryKey: "id",
      writableRoles: [],
      displayColumn: "name",
    },
    {
      name: "package_types",
      labels: { en: "Package types", ru: "РазмерыМеста", zh: "包装规格" },
      primaryKey: "code",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
      displayColumn: "dimensions_label",
    },
    {
      name: "receiving_operations",
      labels: { en: "Receiving operations", ru: "ОперацииПриемкиГруза", zh: "入库操作" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      name: "receiving_operation_lines",
      labels: { en: "Receiving operation lines", ru: "СтрокиПриемкиГруза", zh: "入库操作明细" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      name: "shipping_operations",
      labels: { en: "Shipping operations", ru: "ОперацииОтгрузкиГруза", zh: "出库操作" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      name: "shipping_operation_lines",
      labels: { en: "Shipping operation lines", ru: "СтрокиОтгрузкиГруза", zh: "出库操作明细" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      // Created by 1C and fixed once a trip is formed: administrators only.
      name: "shipping_orders",
      labels: { en: "Shipping orders", ru: "НарядыНаОтгрузку", zh: "发运单" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN],
      displayColumn: "number",
    },
    {
      name: "shipping_order_lines",
      labels: { en: "Shipping order lines", ru: "СтрокиНарядаНаОтгрузку", zh: "发运单明细" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN],
    },
    {
      name: "transfer_operations",
      labels: { en: "Transfer operations", ru: "ОперацииПеремещенияГруза", zh: "调拨操作" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      name: "transfer_operation_lines",
      labels: { en: "Transfer operation lines", ru: "СтрокиПеремещенияГруза", zh: "调拨操作明细" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      name: "receipt_transfer_operations",
      labels: {
        en: "Receipt transfer operations (retired split)",
        ru: "ОперацииПереносаМеждуПЭ (архив)",
        zh: "委托单转移操作（归档）",
      },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      name: "receipt_transfer_operation_lines",
      labels: {
        en: "Receipt transfer operation lines",
        ru: "СтрокиПереносаМеждуПЭ (архив)",
        zh: "货运单转移操作明细",
      },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      name: "warehouses",
      labels: { en: "Warehouses", ru: "Склады", zh: "仓库" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN],
      displayColumn: "name",
    },
    {
      name: "warehouse_locations",
      labels: { en: "Warehouse locations", ru: "СкладскиеЛокации", zh: "库位" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN],
      displayColumn: "code",
    },
    {
      name: "stock_ledger_entries",
      labels: { en: "Stock ledger entries", ru: "РегистрСкладскихОстатков", zh: "库存台账" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN],
    },
    {
      name: "users",
      labels: { en: "Users", ru: "Пользователи", zh: "用户" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN],
      displayColumn: "name",
    },
    {
      name: "files",
      labels: { en: "Files", ru: "Файлы", zh: "文件" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
      displayColumn: "name",
    },
    {
      name: "chat_messages",
      labels: { en: "Chat messages", ru: "СообщенияЧата", zh: "聊天消息" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
    {
      name: "chat_message_files",
      labels: { en: "Chat message files", ru: "ФайлыСообщенийЧата", zh: "聊天消息文件" },
      primaryKey: "id",
      writableRoles: [ROLES.ADMIN, ROLES.STOREKEEPER],
    },
  ] as TableConfig[],

  auth: {
    cookieName: "lk_token",
    /**
     * Outer session-cookie / JWT lifetime, in seconds. Matches the backend
     * refresh token's absolute lifetime (`JWT_REFRESH_TOKEN_EXPIRE_DAYS`, 14d)
     * so an operator who reopens the app the next day is not logged out even
     * though the backend refresh token is still valid. The backend *access*
     * token is short-lived and refreshed independently by `ensureFreshSession`.
     */
    expiresInSeconds: 14 * 24 * 60 * 60, // 14 days
  },

  routes: {
    auth: {
      login: "/api/auth/login",
      logout: "/api/auth/logout",
      me: "/api/auth/me",
    },
    tables: "/api/tables",
    tableData: (table: string) => `/api/tables/${table}`,
    tableRow: (table: string, id: string | number) => `/api/tables/${table}/${id}`,
    /** Compact `{ value, label }` option list for a table, used to populate
     *  <select> dropdowns for foreign-key fields in the create/edit form. */
    tableOptions: (table: string) => `/api/tables/${table}/options`,
    /** Reverse-proxies to the FastAPI warehouse backend's `/api/v1/*` REST API. */
    backend: (path: string) => `/api/backend/${path}`,
  },
} as const;

/** Look up an override entry for a table by name (sync, no DB access). */
export function getTableConfig(name: string): TableConfig | undefined {
  return API_CONFIG.tables.find((t) => t.name === name);
}

export function canWrite(table: TableConfig, role: Role | undefined): boolean {
  if (!role) return false;
  return table.writableRoles.includes(role);
}

export function getTableLabel(table: TableConfig, locale: Locale = defaultLocale): string {
  return table.labels[locale] ?? table.labels[defaultLocale];
}
